<template lang="pug">
.waiter-receive-message-list-tab
  .box-body
    .waiter-opinion-history.d-flex.flex-row.flex-nowrap.align-items-stretch
      // left side list
      .receive-message-list.d-flex.flex-column.receive-message-list-table-component
        // テーブル
        b-table(:items="items" :fields="fields" :current-page="currentPage" :per-page="perPage" :busy="isLoading"
          sticky-header no-border-collapse table-class="message-table opinion-message-table" show-empty)
          // ローディング
          template(#table-busy)
            .text-center.table-loading-message
              b-spinner.spinner
              span.message.align-self-center 読込み中...
          // レコード無し
          template(#empty)
            .empty-message
              span 情報がありません

          // セル：家族ユーザー
          template(#cell(familyMessage)="row")
            .opinion-row(@click="selectOpinion(row.item)" :class="{ selected : row.item.selected }")
              .header.d-flex.align-items-top
                .name(v-if="row.item.functionContents.hidden")
                  span 匿名
                .name(v-else)
                  span {{ row.item.familyName }}
                .date
                  span {{ convertDate(row.item.datetime) }}
              .body.d-flex.align-items-center
                .information
                  .opinion-relation(v-if="row.item.functionContents.hidden")
                    br
                  .opinion-relation(v-else)
                    div
                      span {{ row.item.residentName }}
                  .opinion-last-message
                    div {{ row.item.functionContents.content }}
                .buttons
                  ContactConfirmSwitch(:value="row.item.functionContents.isFinished" @onSwitch="updateOpinion(row.item)")

        .table-options.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap.mr-2
          // ページあたりの行数
          span.table-option-text 1ページの行数:
          .per-page
            b-form-select(id="per-page-select" v-model="perPage" :options="pageOptions" size="md" @change="selectPerPage")
          // ページネーション
          .pagenation
            b-pagination.m-0(v-model="currentPage" :total-rows="rows" :per-page="perPage" :disabled="isLoading" align="center"
              @change="selectPage" hide-goto-end-buttons)

      // right side list
      .message-list.d-flex.flex-column
        .message-list-title
          div(v-if="currentOpinion && 0 < messages.length")
            span(v-if="currentOpinion.functionContents && currentOpinion.functionContents.hidden") 匿名からのメッセージと回答履歴
            span(v-else) {{ currentOpinion.familyName }}様からのメッセージと回答履歴
        .message-content(v-BottomScroll)
          .message(v-for="message in messages")
            .line-message(:class="{ 'line-left': message.senderType == 0, 'line-right': message.senderType == 1 }")
              .text(style="min-height: 39px;")
                template(v-for="content in createSendMessageContents(message)")
                  .text-content.pre-line(v-if="content.type == 'text'")
                    span {{ content.value }}
                  .link-content.text-center(v-else-if="content.type == 'link'" style="padding: 4px;")
                    a(:href="content.uri" target="_blank") {{ content.value }}
                  .image-content(v-else-if="content.type == 'image'")
                    .image
                      img(:src="content.url" @click="showImage(content.url, content.fileName)")
                  .pdf-content(v-else-if="content.type == 'pdf'")
                    .pdf.flex.flex-send-message
                      .title.pre-line 添付ファイル
                      .text.pre-line
                        span PDFの添付ファイルがあります。
                        br
                        span ファイル名：{{ content.fileName }}
                      .link
                        a(@click="showPdf(content)") PDFファイルを開く
              .date {{ convertDate(message.sendTime) }}
          Loading(ref="messageLoading")
        .reply-content(v-if="currentOpinion && 0 < messages.length")
          .d-flex.flex-row.align-items-center.flex-row-reverse
            button.btn.reply-btn(:class="{ 'disable': !existFamily }" :disabled="!existFamily" @click="onReply") 返信する
            .error-message.mr-2(v-if="existFamily == false") ユーザーが削除されているため返信できません

  ImageDownloadModal(v-model="showImageDownloadModal" :url="imageUrl" :fileName="imageFileName")
  PdfModal(v-model="showPdfModal" :pdfUrl="pdfUrl")
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Prop, Watch, Ref, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";
import SystemError from "@/commons/models/SystemError";

import Setting from "../../../../public/setting.json";
import WaiterOpinionsService, { WaiterOpinionMessagesGetModel, WaiterOpinionMessagesPatchModel, WaiterOpinionsGetModel } from "@/commons/services/WaiterOpinions";
import { OpinionFunction } from "@/commons/models/Message";
import WaiterFamiliesService, { WaiterFamiliesGetModel } from "@/commons/services/WaiterFamilies";
import { Account, WaiterReceiveMessagesFunction } from "@/commons/services/Authorization";
import S3UrlService from "@/commons/services/S3Url";

import HelpIcon from "@/pc/basics/HelpIcon.vue";
import BottomScroll from "@/pc/basics/BottomScroll";
import Loading from "@/pc/basics/Loading.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ContactConfirmSwitch from "@/pc/basics/ContactConfirmSwitch.vue";
import ImageDownloadModal from "@/pc/basics/ImageDownloadModal.vue";
import PdfModal from "@/pc/basics/PdfModal.vue";

/**
 * 家族からの連絡画面コンポーネント
 */
@Component({
  components: {
    ContactConfirmSwitch,
    Loading,
    PageLoading,
    ImageDownloadModal,
    PdfModal,
    HelpIcon
  },
  directives: {
    BottomScroll
  }
})
export default class OpinionFamilyListTab extends Vue {
  /** 意見サービス */
  @Inject("WaiterOpinionsService") opinionsService!: WaiterOpinionsService;
  /** 家族ユーザーサービス */
  @Inject("WaiterFamiliesService") familiesService!: WaiterFamiliesService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  @Ref()
  messageLoading!: Loading;

  @Prop()
  /** アカウント */
  account!: Account;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** メッセージ送信確認モーダル表示可否 */
  showSendMessageModal: boolean = false;
  /** メッセージ送信完了モーダル表示可否 */
  showSendMessageSuccessModal: boolean = false;
  /** メッセージ送信失敗モーダル表示可否 */
  showSendMessageFaildModal: boolean = false;

  /** 1行あたりの情報配列 */
  items: Array<WaiterOpinionMessageTableItem> = [];
  /** 列名配列 */
  fields: Array<WaiterOpinionMessageField> = [];
  /** 選択中の意見 */
  currentOpinion: WaiterOpinionMessageTableItem | null = null;
  /** 意見にひもづくメッセージ */
  messages: Array<WaiterOpinionMessagesGetModel> = [];

  /** 現在のページ */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページあたりの表示行数 */
  perPage: number = Number(Setting.waiterOpinion.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.waiterOpinion.pageOptions;
  /** ローディング表示可否 */
  isLoading: boolean = false;

  selectedResidentId: number = -1;
  selectedFamilyId: number = -1;
  existFamily: boolean = false;

  /** 画像モーダルの表示可否 */
  showImageDownloadModal: boolean = false;
  /** 画像モーダルで表示する画像のURL */
  imageUrl: string = "";
  /** 画像のファイル名 */
  imageFileName: string = "";

  /** PDFモーダルの表示可否 */
  showPdfModal: boolean = false;
  /** PDFモーダルで表示するPDFのURL */
  pdfUrl: string = "";

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  get _receiveMessagesFunction(): WaiterReceiveMessagesFunction | null {
    if (this.account) {
      return this.account.permissions.waiterReceiveMessagesFunction;
    } else {
      return null;
    }
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `waiter-receive-message-list-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<WaiterOpinionMessageField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.waiterOpinion.perPage);

    await this.createView();

    return;
  }

  /**
   * ### ご意見の列名を作成する
   */
  public createFields(): Array<WaiterOpinionMessageField> {
    let fields: Array<WaiterOpinionMessageField> = [];
    fields.push({ key: "familyMessage", label: "家族ユーザー", sortable: false, sortDirection: "desc", class: "family-message" });
    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["waiter--receive-message-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    this.items = [];
    this.currentPage = 1;

    // 全体の行数を非同期で取得する
    this.setRows();

    // ご意見テーブルに渡す各プロパティを作成する
    const opinions: Array<WaiterOpinionsGetModel> = await this.getOpinions();
    const items: Array<WaiterOpinionMessageTableItem> = this.createItems(opinions);
    this.items = items;

    this.isLoading = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const count: number = await this.opinionsService.getCount(userStore);
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 表示対象の意見を取得する
   */
  public async getOpinions(): Promise<Array<WaiterOpinionsGetModel>> {
    let response: Array<WaiterOpinionsGetModel> = [];

    try {
      const params: any = {
        order: "desc"
      };
      response = await this.opinionsService.gets(userStore, params);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const pagePerCount: number = 50;
        const count: number = await this.opinionsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / pagePerCount);

        for (let i = 0; i < loopEnd; i++) {
          const params: { startIndex: number; endIndex: number; order: string } = {
            startIndex: i * pagePerCount,
            endIndex: i * pagePerCount + pagePerCount,
            order: "desc"
          };
          response = response.concat(await this.opinionsService.gets(userStore, params));
        }
      }
    }

    // 完了しているか否かを設定する
    // 完了日を見て判断する
    for (let opinion of response) {
      if (opinion.functionContents) {
        if (opinion.functionContents.finished) {
          opinion.functionContents.isFinished = true;
        } else {
          opinion.functionContents.isFinished = false;
        }
      }
    }

    return response;
  }

  /**
   * ### ご意見の行を作成する
   */
  public createItems(opinions: Array<WaiterOpinionsGetModel>): Array<WaiterOpinionMessageTableItem> {
    const items: Array<WaiterOpinionMessageTableItem> = [];
    for (const opinion of opinions) {
      items.push({
        ...opinion,
        selected: false
      });
    }
    return items;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.currentOpinion = null;
    // エラーメッセージをリセットする
    this.$validator.errors.remove("reply-message");
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    this.currentOpinion = null;
    return;
  }

  /**
   * ### 画面で選択したユーザーのメッセージを表示
   */
  public async selectOpinion(opinion: WaiterOpinionMessageTableItem): Promise<void> {
    this.messageLoading.show();

    // 選択された意見のユーザーが存在するかチェックする
    const family: WaiterFamiliesGetModel | null = await this.getFamily(opinion.residentId, opinion.familyId);
    this.existFamily = family != null ? true : false;

    // 選択されている意見を更新
    for (let i = 0; i < this.items.length; i++) {
      if (this.items[i].caseId == opinion.caseId) {
        this.items[i].selected = true;
        this.currentOpinion = this.items[i];
      } else {
        this.items[i].selected = false;
      }
    }

    // 選択された意見が「未確認」であれば「確認済」に更新する
    const _functionContents: any = opinion.functionContents;
    const contents: OpinionFunction = _functionContents;
    if (!contents.finished) {
      this.updateOpinion(opinion);
    }

    // メッセージ取得中に別の問い合わせが選択されることがありえる
    // そのため、取得したメッセージが選択中の問い合わせ以外のメッセージであれば画面更新しない
    this.selectedResidentId = opinion.residentId;
    this.selectedFamilyId = opinion.familyId;
    const messages: Array<WaiterOpinionMessagesGetModel> = await this.createMessages(opinion.caseId);
    if (0 < messages.length && this.currentOpinion && messages[0].caseId == this.currentOpinion.caseId) {
      await this.convertTempFiles(messages);
      this.messages = messages;
    }
    // 画面遷移でコンポーネントが存在しないタイミングがあるため、存在可否をチェックしてからローディングを非表示にする
    if (this.messageLoading) {
      this.messageLoading.hide();
    }

    return;
  }

  /**
   * ### 家族を取得する
   */
  public async getFamily(residentId: number, familyId: number): Promise<WaiterFamiliesGetModel | null> {
    const families: Array<WaiterFamiliesGetModel> = await this.familiesService.getsInFacility(userStore);

    for (const family of families) {
      if (family.id == residentId && family.familyId == familyId) {
        return family;
      }
    }
    return null;
  }

  /**
   * ### 指定したCaseIdに紐づく、画面表示用のメッセージを作成する
   */
  public async createMessages(caseId: number): Promise<Array<WaiterOpinionMessagesGetModel>> {
    let messages: Array<WaiterOpinionMessagesGetModel> = await this.opinionsService.getMessages(userStore, caseId);
    return messages;
  }

  /**
   * ### 添付ファイルURL変換
   *
   * - 添付ファイルがある場合はそのURLを変換する
   */
  public async convertTempFiles(messages: Array<WaiterOpinionMessagesGetModel>): Promise<void> {
    for (const message of messages) {
      if (message.attachedFiles) {
        for (const file of message.attachedFiles) {
          if (file.url) {
            const convertedUrl: string = await this.convertS3Url(file.url);
            file.url = convertedUrl;
          }
          // URLが存在しないことはあり得ないのでシステムエラー
          else {
            throw new SystemError("attachedFiles.type is invalid. sortKey = " + message.waiterOpinionMessagesSortKey);
          }
        }
      }
    }
    return;
  }

  /**
   * ### LINEメッセージ情報のメッセージ種別から判断して、画面表示用のメッセージを作成する
   */
  private async convertS3Url(url: string): Promise<string> {
    // 画像URLが渡されてきた場合はS3にある画像の公開設定を行ってそのURLを画面で使用する
    let publicUrl: string = "";
    try {
      publicUrl = await this.s3UrlService.get(userStore, Setting.backets.imageExpireHour, url);
    } catch (error) {
      // S3の画像が削除されてしまう場合があり得るためシステムエラーにはしない
      console.error("S3 image not fount");
    }
    return publicUrl;
  }

  /**
   * ### 新規投稿日時を画面表示用の文字列に変換する
   */
  public convertDate(date: number) {
    const convertedDate: string = this.$dateUtil.convertNumberToString(date, "yyyy/mm/dd hh:MM");
    return convertedDate;
  }

  public createSendMessageContents(message: WaiterOpinionMessagesGetModel): Array<{ type: string; value: string; uri?: string; url?: string; fileName?: string }> {
    // contentが存在しない場合は空配列を返す
    if (!message.content) {
      return [];
    }

    // template側でlinkボタンとテキストを判別するためにtypeを持たせる
    let contents: Array<{ type: string; value: string; uri?: string; url?: string; fileName?: string }> = [];
    const splitedContent: string[] = message.content.split("\n");
    let buttonCount: number = 1;

    for (const message of splitedContent) {
      // リンクボタンメッセージ(ボタン名指定)
      // 先頭に半角全角スペースがあるだけの場合は無視して扱い、それ以外はテキスト文字列として扱う
      if (message.match(/^\[(.*)\]\((.*)\)$/)) {
        const linkRegex = /\[(.*?)\]\((.*?)\)/g;
        const links: Array<{ name: string; url: string }> = [];
        let match: any;

        while ((match = linkRegex.exec(message)) !== null) {
          const name = match[1] ? match[1] : null;
          const url = match[2] ? match[2] : null;
          links.push({ name, url });
        }

        if (links.length == 1 && links[0].name != null && links[0].url != null) {
          contents.push({
            type: "link",
            value: `${links[0].name}`,
            uri: links[0].url
          });
        } else {
          contents.push({
            type: "text",
            value: message
          });
        }
      }
      // リンクボタンメッセージ(ボタン名指定無し)
      // 先頭に半角全角スペースがあるだけの場合はURLとして扱い、それ以外はテキスト文字列として扱う
      else if (message.match(/^\s*(http|https):\/\/.+/)) {
        // 前後の半角全角スペースを取り除き、URLデコード&エンコードを考慮したURIを作成する
        const uri: string = message.replace(/^\s+|\s+$/g, "");
        const decodedUri: string = decodeURI(uri);
        const encodedUri: string = encodeURI(decodedUri);

        buttonCount++;
        contents.push({
          type: "link",
          value: `リンク その${buttonCount}`,
          uri: encodedUri
        });
      }
      // テキストメッセージ
      else {
        const convertedMessage: string = message == "" ? "　" : message;
        contents.push({
          type: "text",
          value: convertedMessage
        });
      }
    }

    // 添付ファイルがある場合は添付ファイルもコンテンツとする
    if (message.attachedFiles) {
      for (const file of message.attachedFiles) {
        if (file.type == "image") {
          contents.push({
            type: file.type,
            value: "",
            url: file.url,
            fileName: file.name
          });
        } else if (file.type == "pdf") {
          contents.push({
            type: file.type,
            value: "",
            url: file.url,
            fileName: file.name
          });
        }
      }
    }

    return contents;
  }

  /**
   * ### 画像をモーダル表示する
   */
  public showImage(url: string, fileName: string): void {
    this.imageUrl = url;
    const opinion = this.currentOpinion as WaiterOpinionMessageTableItem;
    this.imageFileName = `HitomeQコネクト-受信メッセージ（入所者募集管理）-${opinion.residentName}（${opinion.familyName}）-${fileName}`;
    this.showImageDownloadModal = true;
    return;
  }

  /**
   * ### PDFをモーダル表示する
   */
  public showPdf(file: { type: string; url: string }): void {
    this.pdfUrl = file.url;
    this.showPdfModal = true;
    return;
  }

  /**
   * ### 意見の状態を更新する
   */
  public async updateOpinion(opinion: WaiterOpinionMessageTableItem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    const requestBody: WaiterOpinionMessagesPatchModel = this.createPatchRequestBody(opinion);
    await this.opinionsService.patchMessage(userStore, opinion.caseId, requestBody);

    // サイドメニューの未完了バッジを更新する
    this.$emit("refresh");

    this.isBusy = false;

    return;
  }

  /**
   * ### 意見更新パラメータを作成する
   */
  public createPatchRequestBody(opinion: WaiterOpinionMessageTableItem): WaiterOpinionMessagesPatchModel {
    if (!opinion.functionContents.finished) {
      const date: Date = new Date();
      opinion.functionContents.finished = this.$dateUtil.convertDateToNumber(date, "yyyymmddhhMMss000");
      opinion.functionContents.isFinished = true;
    } else {
      opinion.functionContents.finished = 0;
      opinion.functionContents.isFinished = false;
    }
    // データ破損でnullが格納されている場合があるため、チェックして空文字に置き換える
    opinion.functionContents.content = opinion.functionContents.content ? opinion.functionContents.content : "";
    opinion.functionContents.familyRelation = opinion.functionContents.familyRelation ? opinion.functionContents.familyRelation : "";
    opinion.functionContents.familyPosition = opinion.functionContents.familyPosition ? opinion.functionContents.familyPosition : "";
    return {
      functionType: opinion.functionType,
      functionContents: opinion.functionContents,
      lineId: opinion.lineId,
      messageType: opinion.messageType,
      receiver: opinion.receiver,
      residentId: opinion.residentId,
      residentName: opinion.residentName,
      residentKanaName: opinion.residentKanaName,
      residentRoomName: opinion.residentRoomName,
      familyId: opinion.familyId,
      familyName: opinion.familyName,
      sender: opinion.sender,
      sendTime: opinion.sendTime,
      writtenBy: userStore.loginUser.name,
      writerId: userStore.loginUser.userId,
      caseId: opinion.caseId
    };
  }

  /**
   * ### 返信ボタン押下イベントを親コンポーネントに通知する
   */
  public onReply(): void {
    this.$emit("onReply", {
      currentOpinion: this.currentOpinion,
      messages: this.messages
    });
  }

  /**
   * ### 画面情報を再取得する
   */
  public async refresh(): Promise<void> {
    await this.createView();
    return;
  }
}

export interface WaiterOpinionMessageField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

export interface WaiterOpinionMessageTableItem extends WaiterOpinionsGetModel {
  selected: boolean;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

.waiter-receive-message-list-tab {
  margin-left: auto;
  margin-right: auto;

  @mixin title() {
    background-color: $bg-header;
    padding: 5px;
    min-height: 32px;
    font-weight: bold;
    text-align: center;
  }

  .box {
    margin-bottom: 0;
  }

  .waiter-opinion-history {
    // デフォルトフォントサイズを指定する
    font-size: 16px;
    // 外枠はここで描画させる
    border: solid 1px $border-darkgray;

    // 左側、ユーザーリスト
    .receive-message-list {
      min-width: 50%;
      max-width: 50%;

      .opinion-row {
        padding: 6px 15px;
      }
      .header {
        .name {
          width: calc(100% - 134px);
          padding-right: 12px;
          font-weight: bold;
        }
        .date {
          width: 134px;
          font-size: 14px;
          text-align: right;
        }
      }
      .body {
        .information {
          width: calc(100% - 68px);
          padding-right: 12px;

          .opinion-relation,
          .opinion-last-message {
            div {
              font-size: 14px;
            }
          }
          .opinion-last-message {
            div {
              max-width: calc(14px * 9); // iPadで表示崩れが起きるため、9文字以降は省略
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
        }
        .buttons {
          width: 68px;
        }
      }
      // テーブルのオプション
      // マージン調整、フィルター部分のサイズを他のページより少なくする
      .table-options {
        margin-bottom: 12px;
      }
    }

    // 右側、メッセージリスト
    .message-list {
      min-width: 50%;
      max-width: 50%;
      overflow-y: auto;
      overflow-x: auto;
      border-left: solid 1px $border-darkgray;

      .message-list-title {
        @include title();
        border-bottom: solid 1px $border-lightgray;
        min-height: 35px;
      }

      .message-content {
        overflow-y: scroll;
        overflow-x: auto;
        position: relative;
        max-height: calc(100vh - 324px);
        // 背景色
        background-color: $bg-message;

        .message {
          padding: 10px 0 10px 0;
          // botの発言
          .line-left {
            position: relative;
            display: inline-block;
            margin-bottom: 5px;
            clear: both;
            margin-left: 30px;
            max-width: 740px;
            // 発言本文
            .text {
              padding: 10px;
              border-radius: 20px;
              background-color: $bg-message-bot;
              margin: 0;
              margin-right: 80px;
              // LINEの吹き出しのように見せるために、CSSで図形を描画
              &::after {
                content: "";
                position: absolute;
                display: block;
                width: 0;
                height: 0;
                left: -10px;
                top: 10px;
                border-right: 20px solid $bg-message-bot;
                border-top: 10px solid transparent;
                border-bottom: 10px solid transparent;
              }
            }
            // 日付部分
            .date {
              color: $date-font-color;
              text-align: left;
              font-size: 14px;
              font-weight: bold;
            }
          }
          // 自分の発言
          .line-right {
            position: relative;
            margin-top: 5px;
            float: right;
            clear: both;
            margin-right: 30px;
            max-width: 740px;
            // 発言本文
            .text {
              padding: 10px;
              border-radius: 20px;
              background-color: $bg-message-mine;
              color: rgba(255, 255, 255, 1);
              margin: 0;
              margin-left: 80px;
              // LINEの吹き出しのように見せるために、CSSで図形を描画
              &::after {
                content: "";
                position: absolute;
                display: block;
                width: 0;
                height: 0;
                right: -10px;
                top: 10px;
                border-left: 20px solid $bg-message-mine;
                border-top: 10px solid transparent;
                border-bottom: 10px solid transparent;
              }
            }
            // 日付部分
            .date {
              color: $date-font-color;
              text-align: right;
              font-size: 14px;
              font-weight: bold;
            }
          }
          // LINE風に表示を行うため、vueがメッセージの情報を取得後、スクロールバーの位置を一番下に移動させている
          // vueがDOM要素を作成した後、img要素のsrc属性のURLにGETが走り、画像が取得される
          // 画像の大きさを固定しておかなかった場合、img要素は動的に高さが変わり、スクロールバーの長さが変わる
          // スクロールバーの長さが変わると、スクロール位置もずれてしまい、位置が一番下ではなくなってしまう
          // これを回避するため、画像を表示するdiv要素の高さおよびimg要素の高さを固定し、img要素の高さが動的に変わるのを防ぐ
          .image {
            // TODO 「ここで高さを固定しないと、問題が発生するため、高さ固定」とあるが、max-heightで問題がないか確認する
            // height: 320px;
            max-height: 320px;
            img {
              // 画像サイズは高さ固定
              height: 300px;
              max-width: 700px;
            }
            cursor: pointer;
          }
          .link {
            padding: 8px;
            cursor: pointer;
            a {
              color: $font-key-color;
            }
          }
        }
      }
      .reply-content {
        padding: 8px;
      }
    }
  }
}
</style>
